
import React from 'react';
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FILTER_OPTIONS, FILTER_LABELS } from "@/types/filter-options";

interface PhysicalAppearanceData {
  altura_categoria?: string;
  tipo_corpo?: string;
  cor_cabelo?: string;
  cor_olhos?: string;
}

interface SharedPhysicalAppearanceSectionProps {
  data: PhysicalAppearanceData;
  updateData: (field: keyof PhysicalAppearanceData, value: string) => void;
  isOnboarding?: boolean;
  isEditing?: boolean;
}

const SharedPhysicalAppearanceSection: React.FC<SharedPhysicalAppearanceSectionProps> = ({
  data,
  updateData,
  isOnboarding = false,
  isEditing = true
}) => {
  const labelClass = isOnboarding ? "text-white" : "text-white/90 text-sm";
  const selectClass = isOnboarding 
    ? "bg-white/5 border-white/10 text-white" 
    : "bg-black/20 border-white/20 text-white hover:bg-black/30 transition-colors";
  const contentClass = isOnboarding 
    ? "bg-black/90 border-white/20 text-white" 
    : "bg-black/95 border-white/20 backdrop-blur-xl";
  const itemClass = isOnboarding 
    ? "text-white hover:bg-white/10 hover:text-white focus:bg-white/10 focus:text-white" 
    : "text-white hover:bg-white/10 focus:bg-white/10";

  return (
    <div className="space-y-4">
      {!isOnboarding && !isEditing && (
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-luxury-gold/20 rounded-full flex items-center justify-center">
            <span className="text-lg">👤</span>
          </div>
          <h3 className="text-white font-medium">Aparência Física</h3>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="altura" className={labelClass}>
            Altura {isOnboarding && '*'}
          </Label>
          <Select value={data.altura_categoria || ''} onValueChange={(value) => updateData('altura_categoria', value)}>
            <SelectTrigger className={selectClass}>
              <SelectValue placeholder="Selecione sua altura" />
            </SelectTrigger>
            <SelectContent className={contentClass}>
              {FILTER_OPTIONS.height.map(option => (
                <SelectItem 
                  key={option} 
                  value={option}
                  className={itemClass}
                >
                  {FILTER_LABELS.height[option as keyof typeof FILTER_LABELS.height]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="tipo_corpo" className={labelClass}>
            Tipo Físico {isOnboarding && '*'}
          </Label>
          <Select value={data.tipo_corpo || ''} onValueChange={(value) => updateData('tipo_corpo', value)}>
            <SelectTrigger className={selectClass}>
              <SelectValue placeholder="Selecione seu tipo" />
            </SelectTrigger>
            <SelectContent className={contentClass}>
              {FILTER_OPTIONS.bodyType.map(option => (
                <SelectItem 
                  key={option} 
                  value={option}
                  className={itemClass}
                >
                  {FILTER_LABELS.bodyType[option as keyof typeof FILTER_LABELS.bodyType]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="cor_cabelo" className={labelClass}>Cor do Cabelo</Label>
          <Select value={data.cor_cabelo || ''} onValueChange={(value) => updateData('cor_cabelo', value)}>
            <SelectTrigger className={selectClass}>
              <SelectValue placeholder="Selecione a cor" />
            </SelectTrigger>
            <SelectContent className={contentClass}>
              {FILTER_OPTIONS.hairColor.map(option => (
                <SelectItem 
                  key={option} 
                  value={option}
                  className={itemClass}
                >
                  {FILTER_LABELS.hairColor[option as keyof typeof FILTER_LABELS.hairColor]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="cor_olhos" className={labelClass}>Cor dos Olhos</Label>
          <Select value={data.cor_olhos || ''} onValueChange={(value) => updateData('cor_olhos', value)}>
            <SelectTrigger className={selectClass}>
              <SelectValue placeholder="Selecione a cor" />
            </SelectTrigger>
            <SelectContent className={contentClass}>
              {FILTER_OPTIONS.eyeColor.map(option => (
                <SelectItem 
                  key={option} 
                  value={option}
                  className={itemClass}
                >
                  {FILTER_LABELS.eyeColor[option as keyof typeof FILTER_LABELS.eyeColor]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default SharedPhysicalAppearanceSection;
